import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import InlineAlert from "src/grading/components/InlineAlert/InlineAlert";
import Loader from "src/grading/components/Loader/Loader";
import EntryFrame from "src/grading/layout/EntryFrame";
import { SIGNED_IN_PATH } from "src/grading/pages/Login/Login";
import { allowedRoles, checkRoles } from "src/grading/utils/permissionsHelpers";
import {
  HostedUiError,
  completeHostedUiSignIn,
} from "src/services/auth/hostedUi";
import { useAppDispatch } from "src/store/hooks";
import {
  useLazyGetPermissionsQuery,
  useLazyGetUserQuery,
} from "src/store/reducers/auth/authApiSlice";
import {
  logOut,
  setCredentials,
  setPermissions,
  setUser,
} from "src/store/reducers/auth/authSlice";

/** `/auth/callback`: finishes the hosted-UI sign-in, loads the profile (provisioning it on first login) and permissions. */
const AuthCallbackPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { search } = useLocation();
  const dispatch = useAppDispatch();
  const [getUser] = useLazyGetUserQuery();
  const [getPermissions] = useLazyGetPermissionsQuery();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tokens = await completeHostedUiSignIn(search);
        dispatch(
          setCredentials({
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
          }),
        );
        // Permissions before the profile: the profile lands in the store as soon as it loads and a
        // signed-in user is redirected away from this page (see Login's completeSignIn).
        const permissions = await getPermissions().unwrap();
        if (!checkRoles(allowedRoles, permissions.roles)) {
          dispatch(logOut());
          throw new Error(
            t("auth.staffOnly", {
              defaultValue:
                "This console is for school staff. Students and volunteers use the app.",
            }),
          );
        }
        dispatch(
          setPermissions({
            permissions: permissions.permissions,
            scopes: permissions.scopes,
            mfaRequired: permissions.mfaRequired,
            license: permissions.license,
          }),
        );
        const { profile } = await getUser().unwrap();
        dispatch(setUser({ user: profile }));
        if (!cancelled)
          history.replace(
            permissions.mfaRequired ? "/grading/mfa" : SIGNED_IN_PATH,
          );
      } catch (err: unknown) {
        const e =
          typeof err === "object" && err !== null
            ? (err as { message?: string; data?: { message?: string } })
            : {};
        if (cancelled) return;
        if (err instanceof HostedUiError) {
          setError(t(`auth.${err.code}`, { defaultValue: err.message }));
          return;
        }
        setError(
          // Empty string = generic message, translated at render time.
          e.data?.message ?? e.message ?? "",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [search, dispatch, getUser, getPermissions, history, t]);

  return (
    <EntryFrame
      eyebrow={t("auth.staffSignIn", { defaultValue: "Staff sign-in" })}
      title={
        error !== null
          ? t("auth.callbackErrorTitle", {
              defaultValue: "We couldn't sign you in",
            })
          : t("auth.finishingSignIn", { defaultValue: "Finishing sign-in…" })
      }
    >
      {error !== null ? (
        <Stack spacing={2.5}>
          <InlineAlert severity="error">
            {error ||
              t("auth.callbackError", {
                defaultValue: "We couldn't finish signing you in.",
              })}
          </InlineAlert>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => history.replace("/grading")}
          >
            {t("auth.backToSignIn", { defaultValue: "Back to sign-in" })}
          </Button>
        </Stack>
      ) : (
        <Loader />
      )}
    </EntryFrame>
  );
};

export default AuthCallbackPage;
